import React, { ReactElement } from 'react';
import { FUEL_TYPE } from '@origin-digital/sales-common';
import { CardBackgroundVariant } from '@origin-digital/ods-types';
import {
  IconBoxedReward,
  IconHome,
  IconInternet,
  IconParamountPlusColor,
  IconPromoTag,
  SvgIconProps,
} from '@origin-digital/ods-icons';

export enum PERK_TYPE {
  EDR = 'EDR',
  CREDIT = 'CREDIT',
  PARAMOUNT = 'PARAMOUNT',
  HOME_ASSIST = 'HOME_ASSIST',
  EG_VOUCHER = 'EG_VOUCHER',
  INTERNET = 'INTERNET',
  INTERNET_CONNECTION = 'INTERNET_CONNECTION',
  ENERGY_ADDON = 'ENERGY_ADDON',
}

export interface PerkConfig {
  type: PERK_TYPE;
  title: string;
  alt: string;
  modalHeading: string;
  modalPoints: string[];
  modalFooter: string;
  value: number;
  uptoValue?: boolean;
  fromValue?: boolean;
  backgroundColor: CardBackgroundVariant;
  invertTextColor?: boolean;
  odsIcon: ReactElement<SvgIconProps>;
  iconUrl: string;
  desktopUrl: string;
  salesFormTitle: string;
}

export const PERKS_ALLOWED_COUNT = {
  [FUEL_TYPE.ELECTRICITY]: 3,
  [FUEL_TYPE.GAS]: 1,
};

/**
 * Not all pers are available for value builder perk selection
 */
export type AvailablePerksType = {
  [key in PERK_TYPE]?: PerkConfig;
};

export const AVAILABLE_PERKS: AvailablePerksType = {
  [PERK_TYPE.EDR]: {
    type: PERK_TYPE.EDR,
    title: '10,000 Everyday Rewards points',
    alt: 'Everyday Reward',
    modalHeading: 'Get 10,000 Everyday Rewards points.',
    modalPoints: [
      'Your plan must be active for 30 days.',
      "You'll need your Everyday Rewards membership number to sign up.",
      'You must not have received any other bonus sign-up Everyday Rewards points in the preceding 10-month period for the same fuel.',
      'Points will be applied within 45 days after your acceptance date.',
    ],
    modalFooter: 'T&Cs apply.',
    value: 50,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconBoxedReward),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/logos/edr-logo_90x72.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_edr_2x.png',
    salesFormTitle: '10,000 Everyday Rewards points',
  },
  [PERK_TYPE.PARAMOUNT]: {
    type: PERK_TYPE.PARAMOUNT,
    title: 'Paramount+ for 6 months',
    alt: 'Paramount+',
    modalHeading:
      'Get 6 months of Paramount+ if you subscribe to a Paramount+ Standard Monthly Plan.',
    modalPoints: [
      'After the first 6 months, your plan will auto-renew at the then regular monthly price (currently, $10.99/m) until cancelled.',
      'New or former Paramount+ subscribers only.',
      'Code must be used within 60 days.',
    ],
    modalFooter: 'T&Cs apply.',
    value: 77.94,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconParamountPlusColor, { variant: 'solid', size: 'small' }),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/logos/logo_paramount-plus_1-1.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_paramount_2x.png',
    salesFormTitle: '6 months Paramount+',
  },
  [PERK_TYPE.EG_VOUCHER]: {
    type: PERK_TYPE.EG_VOUCHER,
    title: 'EG eGift card',
    alt: 'EG eGift Card',
    modalHeading: 'Get a $50 EG eGift Card',
    modalPoints: [
      'The EG eGift Card will be emailed to you within approximately 8 business days of signing up to the Origin Value Builder energy plan.',
      'You can redeem this EG eGift Card in store at participating EG Ampol fuel sites.',
    ],
    modalFooter: 'T&Cs apply.',
    value: 50,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconPromoTag, {
      variant: 'solid',
      color: 'secondary',
      size: 'small',
    }),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/logos/eg_logo_72x72.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_fuel_2x.png',
    salesFormTitle: '$50 EG eGift card',
  },
  [PERK_TYPE.CREDIT]: {
    type: PERK_TYPE.CREDIT,
    title: 'Electricity bill credit',
    alt: 'Electricity bill credit',
    modalHeading: 'Get a $50 (inc. GST) electricity bill credit',
    modalPoints: [
      'Credit will be applied to your account within two weeks of sign up if account is still active.',
    ],
    modalFooter: 'T&Cs apply.',
    value: 50,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconPromoTag, {
      variant: 'solid',
      color: 'secondary',
      size: 'small',
    }),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/pictograms/pictogram_value-coins.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_credit_2x.png',
    salesFormTitle: '$50 electricity bill credit',
  },
  [PERK_TYPE.INTERNET]: {
    type: PERK_TYPE.INTERNET,
    title: '50% off internet for 3 months',
    alt: '50% off internet for 3 months',
    modalHeading: 'Get 50% off any internet plan for the first 3 months',
    modalPoints: ['New and moving residential internet customers only'],
    modalFooter: 'T&Cs apply.',
    value: 133.5,
    fromValue: true,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconInternet, {
      variant: 'line',
      color: 'promote',
      size: 'small',
    }),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/pictograms/pictogram_internet-speed-rocket.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_internet_2x.png',
    salesFormTitle: '50% off internet for 3 months',
  },
  [PERK_TYPE.HOME_ASSIST]: {
    type: PERK_TYPE.HOME_ASSIST,
    title: 'Home Assist for 12 months',
    alt: 'Home Assist',
    modalHeading:
      'Get two home emergency callouts valued at up to $300 (inc. GST) each – backed by Allianz Global Assistance (AGA).',
    modalPoints: [
      'Includes AGA’s 24/7 Emergency Home Assistance service – that connects you to qualified tradespeople to attend to common home emergencies including blackouts and leaks from roof damage.',
      'Your 12-month AGA Emergency Home Assistance membership will start on the same day as your energy plan.',
    ],
    modalFooter: 'T&Cs apply.',
    value: 600,
    uptoValue: true,
    backgroundColor: 'promote',
    odsIcon: React.createElement(IconHome, { variant: 'line', color: 'secondary', size: 'small' }),
    iconUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/pictograms/pictogram_maintenance.svg',
    desktopUrl:
      'https://www.originenergy.com.au/cdn/images/originenergy/image/upload/campaignPage/value-builder_ill_home-assist_2x.png',
    salesFormTitle: '12 months Home Assist',
  },
};
